import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { account, common, input, organization } from "../../i18n";

export interface TabMenuData {
  name: string;
  pathname: string;
  disabled?: boolean;
  /** Optional ready-made label; when set, used instead of t(name)
   *  (e.g. for dynamic labels like "Linked Loans (12)"). */
  label?: string;
}

type TabMenuDataType = "main" | "sub" | "underline";

interface Props {
  tabMenuData: TabMenuData[];
  type?: TabMenuDataType;
  /** Controlled (non-route) mode: provide both to drive tabs by state instead
   *  of the URL. `value`/`onChange` use each item's `pathname` as the key. */
  value?: string;
  onChange?: (pathname: string) => void;
  /**
   * Underline only. Both sizes use Inter Medium 16/24 for every tab (the active
   * one is told apart by colour + underline, not weight):
   *  - "page":  12/12 padding, 8px gap — page-level tabs (Figma 7063:12988)
   *  - "dense": 8/12 padding, 4px gap  — tabs inside a modal (Figma 7692:31284)
   * Omit for the original 14px look.
   */
  size?: "page" | "dense";
}

export const TabMenu = (props: Props) => {
  const { pathname } = useLocation();
  const { t } = useTranslation([common, input, account, organization], {
    nsMode: "fallback",
  });

  const isControlled = props.onChange !== undefined;
  const activeKey = isControlled ? props.value : pathname;

  const isMain = props.type === "main" || props.type === undefined;
  const isSub = props.type === "sub";
  const isUnderline = props.type === "underline";
  const lastIndex = props.tabMenuData.length - 1;

  const getItemStyle = (index: number) => {
    const isFirst = index === 0;
    const isLast = index === lastIndex;
    return {
      borderRadius: {
        xs: isFirst ? "50px 50px 0px 0px" : isLast ? "0px 0px 50px 50px" : 0,
        sm: isFirst
          ? "50px 0px 0px 50px !important"
          : isLast
          ? "0px 50px 50px 0px !important"
          : "0 !important",
      },
      ...(isSub && {
        border: "1px solid var(--gray5)",
        borderWidth: isFirst
          ? "1px 0px 1px 1px"
          : isLast
          ? "1px 1px 1px 0px"
          : "1px 0px 1px 0px",
      }),
    };
  };

  if (isUnderline) {
    return (
      <Box
        display="flex"
        gap={props.size === "dense" ? "4px" : "8px"}
        borderBottom="1px solid var(--gray5)"
        sx={{ overflowX: "auto" }}
      >
        {props.tabMenuData.map((e) => {
          const active = activeKey === e.pathname;
          const button = (
            <Button
              type="button"
              variant="text"
              disableRipple
              disabled={e.disabled}
              onClick={
                isControlled && !e.disabled
                  ? () => props.onChange?.(e.pathname)
                  : undefined
              }
              sx={{
                textTransform: "none",
                borderRadius: 0,
                // Figma draws the 2px active underline inside the tab and on top
                // of the row's 1px rule (page row 48, dense row 40). So the
                // bottom padding gives up the underline and the tab overlaps
                // the rule by 1px: 12 + 24 + 10 + 2 - 1 = 47 + 1px rule = 48.
                padding:
                  props.size === "dense"
                    ? "8px 12px 6px"
                    : props.size === "page"
                    ? "12px 12px 10px"
                    : "10px 12px",
                ...(props.size && { marginBottom: "-1px" }),
                minWidth: 0,
                color: active ? "var(--green)" : "var(--gray1)",
                borderBottom: active
                  ? "2px solid var(--green)"
                  : "2px solid transparent",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "var(--green)",
                },
              }}
            >
              <Typography
                variant="h4"
                fontWeight={props.size ? 500 : active ? 600 : 500}
                // Render as <span>: alert.css pins every <h4> to 14px !important
                // app-wide, which would override the Figma 16px here.
                {...(props.size && {
                  component: "span",
                  fontSize: "16px",
                  lineHeight: "24px",
                })}
              >
                {e.label ?? t(e.name)}
              </Typography>
            </Button>
          );
          return e.disabled || isControlled ? (
            <Box key={e.pathname}>{button}</Box>
          ) : (
            <Link key={e.pathname} to={e.pathname}>
              {button}
            </Link>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: isMain ? "10px 40px" : "0px",
        backgroundColor: isMain ? "#F2F2F2" : "transparent",
      }}
    >
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }}>
        {props.tabMenuData.map((e, index) => {
          const button = (
            <Button
              type="button"
              variant="contained"
              color={activeKey === e.pathname ? "green" : "white"}
              disableElevation
              disabled={e.disabled}
              onClick={
                isControlled && !e.disabled
                  ? () => props.onChange?.(e.pathname)
                  : undefined
              }
              sx={{ width: "100%", height: "100%", padding: "9px 0px", ...getItemStyle(index) }}
            >
              <Typography fontWeight={500} alignContent="center" variant="h3">
                {e.label ?? t(e.name)}
              </Typography>
            </Button>
          );

          return (
            <Box flex={1} key={e.pathname}>
              {e.disabled || isControlled ? (
                button
              ) : (
                <Link to={e.pathname}>{button}</Link>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
